<script setup>
import { reactive, ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, between } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAssistantSettingsStore } from 'dashboard/store/assistant/settings';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import AssistantScenariosAPI from 'dashboard/api/assistant/scenarios';
import AssistantAgentsAPI from 'dashboard/api/assistant/agents';
import AssistantMcpServersAPI from 'dashboard/api/assistant/mcpServers';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['edit', 'create'].includes(value),
  },
  agent: {
    type: Object,
    default: () => ({}),
  },
  kind: {
    type: String,
    default: 'customer_agent',
    validator: value =>
      ['customer_agent', 'operator_assistant'].includes(value),
  },
});

const emit = defineEmits(['submit', 'cancel', 'delete']);

const { t } = useI18n();
const store = useStore();
const settingsStore = useAssistantSettingsStore();

const formState = {
  uiFlags: useMapGetter('assistantAgents/getUIFlags'),
  knowledgeBases: useMapGetter('assistantKnowledgeBases/getRecords'),
  customTools: useMapGetter('assistantCustomTools/getRecords'),
  // Р16 п.6: операторы аккаунта — для назначения ассистента оператора.
  operators: useMapGetter('agents/getAgents'),
};

const initialState = {
  name: '',
  description: '',
  productName: '',
  welcomeMessage: '',
  handoffMessage: '',
  resolutionMessage: '',
  instructions: '',
  temperature: 1,
  provider: '',
  model: '',
  featureFaq: false,
  allowHandoff: true,
  autoResolve: false,
  askCsat: false,
  handoffRespectHours: false,
  offlineHandoffMessage: '',
  // Р73: сколько раз агент пробует сам, прежде чем звать оператора (1 — сразу).
  handoffAttempts: 1,
  handoffRetryMessage: '',
  // Р72: строгая сверка фактов ответа с ответами инструментов.
  strictFacts: false,
  guardFallback: '',
  knowledgeBaseIds: [],
  customToolIds: [],
  linkedScenarioIds: [],
  mcpServerIds: [],
  operatorUserIds: [],
};

const state = reactive({ ...initialState });

// Ассистент оператора не общается с клиентами (Р2) — клиентские поля
// (продукт, приветствие, хендофф, CSAT и т.п.) для него скрыты.
const isOperatorAssistant = computed(
  () => (props.agent?.kind || props.kind) === 'operator_assistant'
);

// Р10 п.6: у ассистента свои тексты полей — «ассистент», не «ИИ-агент».
const formSection = computed(() =>
  isOperatorAssistant.value
    ? 'ASSISTANT.OPERATOR_ASSISTANTS.FORM'
    : 'ASSISTANT.AGENTS.FORM'
);

const validationRules = computed(() => ({
  name: { required, minLength: minLength(1) },
  description: { required, minLength: minLength(1) },
  productName: isOperatorAssistant.value
    ? {}
    : { required, minLength: minLength(1) },
  temperature: { between: between(0, 1) },
}));

const v$ = useVuelidate(validationRules, state);

const isLoading = computed(
  () =>
    formState.uiFlags.value.creatingItem || formState.uiFlags.value.updatingItem
);

// Р9.2: модель выбирается парой «провайдер + модель» из ПОДКЛЮЧЁННЫХ
// провайдеров аккаунта; у «своего» провайдера каталога нет — модель вводится
// текстом. Пусто — используется первое подключение и его первая модель.
const connectedProviders = computed(() => settingsStore.credentials || []);

const providerLabel = provider =>
  (settingsStore.providers || []).find(item => item.value === provider)
    ?.label || provider;

const effectiveProvider = computed(
  () => state.provider || connectedProviders.value[0]?.provider || ''
);

// Р10 п.1: модели берутся из живого каталога подключения (синк по API
// провайдера, только текстовые/чатовые); статический список — запасной.
const modelOptions = computed(() => {
  const credential = connectedProviders.value.find(
    item => item.provider === effectiveProvider.value
  );
  const dynamic = credential?.models || [];
  return dynamic.length
    ? dynamic
    : settingsStore.getModelsForProvider(effectiveProvider.value);
});

// Для провайдера без каталога моделей (custom) — свободный ввод.
const isFreeformModel = computed(
  () => !!effectiveProvider.value && !modelOptions.value.length
);

// Р11 п.6: «модели по умолчанию» нет — провайдер и модель проставляются
// автоматически (первое подключение / первая модель), пользователь может
// переключить. Пустого варианта в выпадающем списке больше нет.
const ensureProviderAndModel = () => {
  if (!state.provider && connectedProviders.value.length) {
    state.provider = connectedProviders.value[0].provider;
  }
  const options = modelOptions.value;
  if (options.length && !options.includes(state.model)) {
    state.model = options[0];
  }
};

watch(
  () => state.provider,
  () => {
    // Смена провайдера подставляет первую модель нового провайдера.
    if (!modelOptions.value.includes(state.model)) {
      state.model = modelOptions.value[0] || '';
    }
  }
);

// Живой список моделей (Р11 п.7): при открытии формы фонов­о обновляем каталог
// каждого подключения по API провайдера — список всегда актуален, без ручной
// кнопки. Кэш показывается сразу, свежие данные подставляются по готовности.
watch(modelOptions, () => ensureProviderAndModel(), { immediate: false });

const refreshProviderModels = async () => {
  const jobs = connectedProviders.value.map(credential =>
    settingsStore.syncCredentialModels(credential.id).catch(() => null)
  );
  await Promise.allSettled(jobs);
  ensureProviderAndModel();
};

const toggleKnowledgeBase = id => {
  if (state.knowledgeBaseIds.includes(id)) {
    state.knowledgeBaseIds = state.knowledgeBaseIds.filter(kbId => kbId !== id);
  } else {
    state.knowledgeBaseIds = [...state.knowledgeBaseIds, id];
  }
};

const toggleCustomTool = id => {
  if (state.customToolIds.includes(id)) {
    state.customToolIds = state.customToolIds.filter(toolId => toolId !== id);
  } else {
    state.customToolIds = [...state.customToolIds, id];
  }
};

// MCP-серверы (Р9.4): подключаются к агенту целиком, инструменты сервера
// становятся доступны агенту.
const mcpServers = ref([]);

const fetchMcpServers = async () => {
  try {
    const { data } = await AssistantMcpServersAPI.get();
    mcpServers.value = data.payload || [];
  } catch (error) {
    mcpServers.value = [];
  }
};

const toggleMcpServer = id => {
  if (state.mcpServerIds.includes(id)) {
    state.mcpServerIds = state.mcpServerIds.filter(serverId => serverId !== id);
  } else {
    state.mcpServerIds = [...state.mcpServerIds, id];
  }
};

// Сценарии — общий каталог аккаунта (Р8.1); в форме подключаются связками.
const accountScenarios = ref([]);

const fetchScenarios = async () => {
  try {
    const { data } = await AssistantScenariosAPI.getAll();
    accountScenarios.value = data.payload || [];
  } catch (error) {
    accountScenarios.value = [];
  }
};

// Р16 п.6: назначение ассистента оператора конкретным операторам (админ).
const toggleOperator = id => {
  if (state.operatorUserIds.includes(id)) {
    state.operatorUserIds = state.operatorUserIds.filter(uid => uid !== id);
  } else {
    state.operatorUserIds = [...state.operatorUserIds, id];
  }
};

const toggleScenario = id => {
  if (state.linkedScenarioIds.includes(id)) {
    state.linkedScenarioIds = state.linkedScenarioIds.filter(
      scenarioId => scenarioId !== id
    );
  } else {
    state.linkedScenarioIds = [...state.linkedScenarioIds, id];
  }
};

const getErrorMessage = (field, errorKey) => {
  return v$.value[field].$error
    ? t(`ASSISTANT.AGENTS.FORM.${errorKey}.ERROR`)
    : '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name', 'NAME'),
  description: getErrorMessage('description', 'DESCRIPTION'),
  productName: getErrorMessage('productName', 'PRODUCT_NAME'),
  temperature: getErrorMessage('temperature', 'TEMPERATURE'),
}));

const handleCancel = () => emit('cancel');

// Р11 п.3/12: список источников, где подключён агент (только клиентские).
const connectedInboxNames = computed(() =>
  (props.agent?.inboxes || []).map(inbox => inbox.name)
);

const isEdit = computed(() => props.mode === 'edit');

const prepareAgentDetails = () => ({
  name: state.name,
  description: state.description,
  kind: props.agent?.kind || props.kind,
  knowledge_base_ids: state.knowledgeBaseIds,
  custom_tool_ids: state.customToolIds,
  linked_scenario_ids: state.linkedScenarioIds,
  mcp_server_ids: state.mcpServerIds,
  operator_user_ids: state.operatorUserIds,
  config: {
    product_name: state.productName,
    welcome_message: state.welcomeMessage,
    handoff_message: state.handoffMessage,
    resolution_message: state.resolutionMessage,
    instructions: state.instructions,
    temperature: Number(state.temperature),
    feature_faq: state.featureFaq,
    allow_handoff: state.allowHandoff,
    auto_resolve: state.autoResolve,
    ask_csat: state.askCsat,
    handoff_respect_hours: state.handoffRespectHours,
    handoff_attempts: Number(state.handoffAttempts) || 1,
    strict_facts: state.strictFacts,
    ...(state.offlineHandoffMessage.trim() && {
      offline_handoff_message: state.offlineHandoffMessage.trim(),
    }),
    ...(state.handoffRetryMessage.trim() && {
      handoff_retry_message: state.handoffRetryMessage.trim(),
    }),
    ...(state.guardFallback.trim() && {
      guard_fallback: state.guardFallback.trim(),
    }),
    // Модель/провайдер выбираются явно (Р11 п.6 — без «по умолчанию»).
    ...(state.model.trim() && { model: state.model.trim() }),
    ...(state.provider && { provider: state.provider }),
  },
});

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) {
    return;
  }

  emit('submit', prepareAgentDetails());
};

const updateStateFromAgent = agent => {
  if (!agent) return;

  const { name, description, config = {} } = agent;

  Object.assign(state, {
    name,
    description,
    knowledgeBaseIds: agent.knowledge_base_ids || [],
    customToolIds: agent.custom_tool_ids || [],
    linkedScenarioIds: agent.linked_scenario_ids || [],
    mcpServerIds: agent.mcp_server_ids || [],
    operatorUserIds: agent.operator_user_ids || [],
    productName: config.product_name || '',
    welcomeMessage: config.welcome_message || '',
    handoffMessage: config.handoff_message || '',
    resolutionMessage: config.resolution_message || '',
    instructions: config.instructions || '',
    temperature: config.temperature ?? 1,
    provider: config.provider || '',
    model: config.model || '',
    featureFaq: config.feature_faq || false,
    allowHandoff: config.allow_handoff ?? true,
    autoResolve: config.auto_resolve || false,
    askCsat: config.ask_csat || false,
    handoffRespectHours: config.handoff_respect_hours || false,
    offlineHandoffMessage: config.offline_handoff_message || '',
    handoffAttempts: config.handoff_attempts || 1,
    handoffRetryMessage: config.handoff_retry_message || '',
    strictFacts: config.strict_facts || false,
    guardFallback: config.guard_fallback || '',
  });
  ensureProviderAndModel();
};

watch(
  () => props.agent,
  newAgent => {
    // Prefill on edit, and also on create when seeded from a niche template
    // (the template arrives as an agent-shaped object with a config).
    if (newAgent && (props.mode === 'edit' || newAgent.config)) {
      updateStateFromAgent(newAgent);
    }
  },
  { immediate: true }
);

// Свежая гидратация при редактировании: карточка в списке могла устареть
// (связки меняются и с других страниц), а сохранение формы заменяет наборы
// связок целиком — иначе стёрлись бы связки, созданные после открытия списка.
const rehydrateAgent = async () => {
  if (props.mode !== 'edit' || !props.agent?.id) return;
  try {
    const { data } = await AssistantAgentsAPI.show(props.agent.id);
    updateStateFromAgent(data);
  } catch (error) {
    // остаёмся на данных из props
  }
};

onMounted(async () => {
  if (!settingsStore.providers.length) await settingsStore.fetch();
  else await settingsStore.fetch();
  store.dispatch('assistantKnowledgeBases/get');
  store.dispatch('assistantCustomTools/get');
  fetchMcpServers();
  if (isOperatorAssistant.value) store.dispatch('agents/get');
  if (!isOperatorAssistant.value) fetchScenarios();
  ensureProviderAndModel();
  refreshProviderModels();
  rehydrateAgent();
});
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.name"
      :label="t(`${formSection}.NAME.LABEL`)"
      :placeholder="t(`${formSection}.NAME.PLACEHOLDER`)"
      :message="formErrors.name"
      :message-type="formErrors.name ? 'error' : 'info'"
    />

    <TextArea
      v-model="state.description"
      :label="t(`${formSection}.DESCRIPTION.LABEL`)"
      :placeholder="t(`${formSection}.DESCRIPTION.PLACEHOLDER`)"
      :max-length="2000"
      :message="formErrors.description"
      :message-type="formErrors.description ? 'error' : 'info'"
    />

    <template v-if="!isOperatorAssistant">
      <Input
        v-model="state.productName"
        :label="t('ASSISTANT.AGENTS.FORM.PRODUCT_NAME.LABEL')"
        :placeholder="t('ASSISTANT.AGENTS.FORM.PRODUCT_NAME.PLACEHOLDER')"
        :message="formErrors.productName"
        :message-type="formErrors.productName ? 'error' : 'info'"
      />

      <Input
        v-model="state.welcomeMessage"
        :label="t('ASSISTANT.AGENTS.FORM.WELCOME_MESSAGE.LABEL')"
        :placeholder="t('ASSISTANT.AGENTS.FORM.WELCOME_MESSAGE.PLACEHOLDER')"
      />

      <Input
        v-model="state.handoffMessage"
        :label="t('ASSISTANT.AGENTS.FORM.HANDOFF_MESSAGE.LABEL')"
        :placeholder="t('ASSISTANT.AGENTS.FORM.HANDOFF_MESSAGE.PLACEHOLDER')"
        :message="t('ASSISTANT.AGENTS.FORM.HANDOFF_MESSAGE.HELP_TEXT')"
      />

      <Input
        v-if="state.handoffRespectHours"
        v-model="state.offlineHandoffMessage"
        :label="t('ASSISTANT.AGENTS.FORM.OFFLINE_HANDOFF_MESSAGE.LABEL')"
        :placeholder="
          t('ASSISTANT.AGENTS.FORM.OFFLINE_HANDOFF_MESSAGE.PLACEHOLDER')
        "
        :message="t('ASSISTANT.AGENTS.FORM.OFFLINE_HANDOFF_MESSAGE.HELP_TEXT')"
      />

      <Input
        v-model="state.resolutionMessage"
        :label="t('ASSISTANT.AGENTS.FORM.RESOLUTION_MESSAGE.LABEL')"
        :placeholder="t('ASSISTANT.AGENTS.FORM.RESOLUTION_MESSAGE.PLACEHOLDER')"
      />
    </template>

    <TextArea
      v-model="state.instructions"
      :label="t(`${formSection}.INSTRUCTIONS.LABEL`)"
      :placeholder="t(`${formSection}.INSTRUCTIONS.PLACEHOLDER`)"
      :max-length="20000"
      auto-height
    />

    <Input
      v-model="state.temperature"
      type="number"
      min="0"
      max="1"
      step="0.1"
      :label="t('ASSISTANT.AGENTS.FORM.TEMPERATURE.LABEL')"
      :message="formErrors.temperature"
      :message-type="formErrors.temperature ? 'error' : 'info'"
    />

    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('ASSISTANT.AGENTS.FORM.PROVIDER.LABEL') }}
        </span>
        <select
          v-model="state.provider"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
        >
          <option value="">
            {{ t('ASSISTANT.AGENTS.FORM.PROVIDER.PLACEHOLDER') }}
          </option>
          <option
            v-for="credential in connectedProviders"
            :key="credential.provider"
            :value="credential.provider"
          >
            {{ providerLabel(credential.provider) }}
          </option>
        </select>
        <span v-if="!connectedProviders.length" class="text-xs text-n-slate-11">
          {{ t('ASSISTANT.AGENTS.FORM.PROVIDER.EMPTY') }}
        </span>
      </label>

      <label class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('ASSISTANT.AGENTS.FORM.MODEL.LABEL') }}
        </span>
        <input
          v-if="isFreeformModel"
          v-model="state.model"
          type="text"
          :placeholder="t('ASSISTANT.AGENTS.FORM.MODEL.FREEFORM_PLACEHOLDER')"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
        />
        <select
          v-else
          v-model="state.model"
          class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12"
        >
          <option v-for="model in modelOptions" :key="model" :value="model">
            {{ model }}
          </option>
        </select>
        <span class="text-xs text-n-slate-11">
          {{ t('ASSISTANT.AGENTS.FORM.MODEL.HELP_TEXT') }}
        </span>
      </label>
    </div>

    <fieldset class="flex flex-col gap-2.5">
      <legend class="mb-1 text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.AGENTS.FORM.KNOWLEDGE_BASES.LABEL') }}
      </legend>
      <p class="mb-1 text-xs text-n-slate-11">
        {{ t('ASSISTANT.AGENTS.FORM.KNOWLEDGE_BASES.HELP_TEXT') }}
      </p>
      <p
        v-if="!formState.knowledgeBases.value.length"
        class="text-xs text-n-slate-11"
      >
        {{ t('ASSISTANT.AGENTS.FORM.KNOWLEDGE_BASES.EMPTY') }}
      </p>
      <label
        v-for="knowledgeBase in formState.knowledgeBases.value"
        :key="knowledgeBase.id"
        class="flex items-center gap-2"
      >
        <input
          type="checkbox"
          :checked="state.knowledgeBaseIds.includes(knowledgeBase.id)"
          @change="toggleKnowledgeBase(knowledgeBase.id)"
        />
        <span class="text-sm text-n-slate-12">{{ knowledgeBase.name }}</span>
      </label>
    </fieldset>

    <fieldset class="flex flex-col gap-2.5">
      <legend class="mb-1 text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.AGENTS.FORM.TOOLS.LABEL') }}
      </legend>
      <p class="mb-1 text-xs text-n-slate-11">
        {{ t('ASSISTANT.AGENTS.FORM.TOOLS.HELP_TEXT') }}
      </p>
      <p
        v-if="!formState.customTools.value.length"
        class="text-xs text-n-slate-11"
      >
        {{ t('ASSISTANT.AGENTS.FORM.TOOLS.EMPTY') }}
      </p>
      <label
        v-for="tool in formState.customTools.value"
        :key="tool.id"
        class="flex items-center gap-2"
      >
        <input
          type="checkbox"
          :checked="state.customToolIds.includes(tool.id)"
          @change="toggleCustomTool(tool.id)"
        />
        <span class="text-sm text-n-slate-12">{{ tool.title }}</span>
      </label>
    </fieldset>

    <fieldset v-if="isOperatorAssistant" class="flex flex-col gap-2.5">
      <legend class="mb-1 text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.OPERATOR_ASSISTANTS.FORM.OPERATORS.LABEL') }}
      </legend>
      <p class="mb-1 text-xs text-n-slate-11">
        {{ t('ASSISTANT.OPERATOR_ASSISTANTS.FORM.OPERATORS.HELP_TEXT') }}
      </p>
      <p
        v-if="!formState.operators.value.length"
        class="text-xs text-n-slate-11"
      >
        {{ t('ASSISTANT.OPERATOR_ASSISTANTS.FORM.OPERATORS.EMPTY') }}
      </p>
      <label
        v-for="operator in formState.operators.value"
        :key="operator.id"
        class="flex items-center gap-2"
      >
        <input
          type="checkbox"
          :checked="state.operatorUserIds.includes(operator.id)"
          @change="toggleOperator(operator.id)"
        />
        <span class="text-sm text-n-slate-12">{{ operator.name }}</span>
      </label>
    </fieldset>

    <fieldset v-if="!isOperatorAssistant" class="flex flex-col gap-2.5">
      <legend class="mb-1 text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.AGENTS.FORM.SCENARIOS.LABEL') }}
      </legend>
      <p class="mb-1 text-xs text-n-slate-11">
        {{ t('ASSISTANT.AGENTS.FORM.SCENARIOS.HELP_TEXT') }}
      </p>
      <p v-if="!accountScenarios.length" class="text-xs text-n-slate-11">
        {{ t('ASSISTANT.AGENTS.FORM.SCENARIOS.EMPTY') }}
      </p>
      <label
        v-for="scenario in accountScenarios"
        :key="scenario.id"
        class="flex items-center gap-2"
      >
        <input
          type="checkbox"
          :checked="state.linkedScenarioIds.includes(scenario.id)"
          @change="toggleScenario(scenario.id)"
        />
        <span class="text-sm text-n-slate-12">{{ scenario.title }}</span>
      </label>
    </fieldset>

    <fieldset v-if="mcpServers.length" class="flex flex-col gap-2.5">
      <legend class="mb-1 text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.AGENTS.FORM.MCP_SERVERS.LABEL') }}
      </legend>
      <p class="mb-1 text-xs text-n-slate-11">
        {{ t('ASSISTANT.AGENTS.FORM.MCP_SERVERS.HELP_TEXT') }}
      </p>
      <label
        v-for="server in mcpServers"
        :key="server.id"
        class="flex items-center gap-2"
      >
        <input
          type="checkbox"
          :checked="state.mcpServerIds.includes(server.id)"
          @change="toggleMcpServer(server.id)"
        />
        <span class="text-sm text-n-slate-12">
          {{ server.name }}
          <span class="text-xs text-n-slate-11">
            ({{
              t('ASSISTANT.AGENTS.FORM.MCP_SERVERS.TOOLS_COUNT', {
                count: server.tools_count || 0,
              })
            }})
          </span>
        </span>
      </label>
    </fieldset>

    <fieldset v-if="!isOperatorAssistant" class="flex flex-col gap-3">
      <legend class="mb-3 text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.AGENTS.FORM.BEHAVIOUR.TITLE') }}
      </legend>

      <div class="flex items-start gap-2">
        <Switch v-model="state.allowHandoff" class="mt-0.5" />
        <div class="flex flex-col gap-0.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ASSISTANT.AGENTS.FORM.ALLOW_HANDOFF.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('ASSISTANT.AGENTS.FORM.ALLOW_HANDOFF.HELP_TEXT') }}
          </span>
        </div>
      </div>

      <div class="flex items-start gap-2">
        <Switch v-model="state.autoResolve" class="mt-0.5" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('ASSISTANT.AGENTS.FORM.AUTO_RESOLVE.LABEL') }}
        </span>
      </div>

      <div class="flex items-start gap-2">
        <Switch v-model="state.askCsat" class="mt-0.5" />
        <div class="flex flex-col gap-0.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ASSISTANT.AGENTS.FORM.ASK_CSAT.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('ASSISTANT.AGENTS.FORM.ASK_CSAT.HELP_TEXT') }}
          </span>
        </div>
      </div>

      <div class="flex items-start gap-2">
        <Switch v-model="state.handoffRespectHours" class="mt-0.5" />
        <div class="flex flex-col gap-0.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ASSISTANT.AGENTS.FORM.HANDOFF_RESPECT_HOURS.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('ASSISTANT.AGENTS.FORM.HANDOFF_RESPECT_HOURS.HELP_TEXT') }}
          </span>
        </div>
      </div>

      <!-- Р73: оператора зовём не с первой просьбы — клиент часто просит его
           рефлекторно, ещё не объяснив задачу. -->
      <div v-if="state.allowHandoff" class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('ASSISTANT.AGENTS.FORM.HANDOFF_ATTEMPTS.LABEL') }}
        </label>
        <select
          v-model.number="state.handoffAttempts"
          class="w-full h-10 px-3 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12"
        >
          <option :value="1">
            {{ t('ASSISTANT.AGENTS.FORM.HANDOFF_ATTEMPTS.IMMEDIATE') }}
          </option>
          <option :value="2">
            {{ t('ASSISTANT.AGENTS.FORM.HANDOFF_ATTEMPTS.SECOND') }}
          </option>
          <option :value="3">
            {{ t('ASSISTANT.AGENTS.FORM.HANDOFF_ATTEMPTS.THIRD') }}
          </option>
        </select>
        <span class="text-xs text-n-slate-11">
          {{ t('ASSISTANT.AGENTS.FORM.HANDOFF_ATTEMPTS.HELP_TEXT') }}
        </span>
      </div>

      <Input
        v-if="state.allowHandoff && state.handoffAttempts > 1"
        v-model="state.handoffRetryMessage"
        :label="t('ASSISTANT.AGENTS.FORM.HANDOFF_RETRY_MESSAGE.LABEL')"
        :placeholder="
          t('ASSISTANT.AGENTS.FORM.HANDOFF_RETRY_MESSAGE.PLACEHOLDER')
        "
      />

      <!-- Р72: сверка фактов ответа с ответами инструментов. Подходит агенту,
           у которого расписание живёт во внешней системе, и мешает тому, кто
           отвечает из базы знаний. -->
      <div class="flex items-start gap-2">
        <Switch v-model="state.strictFacts" class="mt-0.5" />
        <div class="flex flex-col gap-0.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ASSISTANT.AGENTS.FORM.STRICT_FACTS.LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('ASSISTANT.AGENTS.FORM.STRICT_FACTS.HELP_TEXT') }}
          </span>
        </div>
      </div>

      <Input
        v-if="state.strictFacts"
        v-model="state.guardFallback"
        :label="t('ASSISTANT.AGENTS.FORM.GUARD_FALLBACK.LABEL')"
        :placeholder="t('ASSISTANT.AGENTS.FORM.GUARD_FALLBACK.PLACEHOLDER')"
      />

      <!-- Р10 п.2: подтверждение телефона убрано (сбор — на уровне источника).
           Р11 п.1: лимит ответов убран — диалог по номеру живёт долго, агент
           не «замолкает»; от зацикливания защищает анти-луп на бэкенде. -->
    </fieldset>

    <fieldset v-if="!isOperatorAssistant" class="flex flex-col gap-2.5">
      <legend class="mb-3 text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.AGENTS.FORM.FEATURES.TITLE') }}
      </legend>

      <label class="flex items-center gap-2">
        <input v-model="state.featureFaq" type="checkbox" />
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('ASSISTANT.AGENTS.FORM.FEATURES.ALLOW_CONVERSATION_FAQS') }}
        </span>
      </label>
      <!-- Р10 п.5: чекбокс «заметки-воспоминания» убран — функция ещё не
           реализована, не путаем пользователя. -->
    </fieldset>

    <!-- Р11 п.3/12: где подключён агент (только редактирование клиентского). -->
    <div
      v-if="isEdit && !isOperatorAssistant"
      class="flex flex-col gap-1.5 p-3 rounded-xl border border-n-weak"
    >
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('ASSISTANT.AGENTS.FORM.CONNECTED_INBOXES.LABEL') }}
      </span>
      <div v-if="connectedInboxNames.length" class="flex flex-wrap gap-1.5">
        <span
          v-for="inboxName in connectedInboxNames"
          :key="inboxName"
          class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-12"
        >
          {{ inboxName }}
        </span>
      </div>
      <span v-else class="text-xs text-n-slate-11">
        {{ t('ASSISTANT.AGENTS.FORM.CONNECTED_INBOXES.EMPTY') }}
      </span>
    </div>

    <!-- Р11 п.3/4: удаление — внутри формы редактирования. -->
    <div v-if="isEdit" class="flex justify-start">
      <Button
        type="button"
        variant="ghost"
        color="ruby"
        icon="i-lucide-trash-2"
        :label="t('ASSISTANT.FORM.DELETE')"
        @click="emit('delete')"
      />
    </div>

    <div class="flex items-center justify-between w-full gap-3">
      <Button
        type="button"
        variant="faded"
        color="slate"
        :label="t('ASSISTANT.FORM.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        type="submit"
        :label="t(`ASSISTANT.FORM.${mode.toUpperCase()}`)"
        class="w-full"
        :is-loading="isLoading"
        :disabled="isLoading"
      />
    </div>
  </form>
</template>
